import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Planet3D from "@/components/Planet3D";
import PlotSelectionModal from "@/components/PlotSelectionModal";
import api from "@/lib/api";
import { MapPin, Sparkles } from "lucide-react";

export default function ExplorePlanet() {
  const { planet } = useParams();
  const [markers, setMarkers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/markers/${planet}`).then((r) => setMarkers(r.data));
  }, [planet]);

  const openMarker = (m) => {
    setSelected(m);
    setOpen(true);
  };

  const title = planet === "moon" ? "The Moon" : "Mars";
  const subtitle = planet === "moon" ? "Sea-of-Tranquility Registry · Lunar Curation" : "Elysium & Beyond · Martian Curation";

  return (
    <div className="relative z-10 pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* 3D Canvas */}
        <div className="glass rounded-3xl overflow-hidden relative h-[75vh]">
          <div className="absolute top-6 left-6 z-10">
            <div className="overline text-gold">{subtitle}</div>
            <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="explore-title">{title}</h1>
          </div>
          <div className="absolute bottom-6 left-6 z-10 text-xs text-white/50 font-mono-space">
            DRAG TO ROTATE · SCROLL TO ZOOM · CLICK A MARKER TO SELECT
          </div>
          <Planet3D
            planet={planet}
            interactive
            markers={markers}
            onMarkerClick={openMarker}
            activeMarkerId={selected?.id}
            autoRotate={false}
          />
        </div>

        {/* Side panel */}
        <aside className="glass rounded-3xl p-6 h-[75vh] overflow-y-auto">
          <div className="overline text-gold mb-3">Available Locations</div>
          <div className="font-display text-2xl font-bold tracking-tight mb-6">
            {markers.length} curated {planet === "moon" ? "lunar" : "martian"} sites
          </div>
          <div className="space-y-2">
            {markers.map((m) => (
              <button
                key={m.id}
                onClick={() => openMarker(m)}
                data-testid={`marker-btn-${m.id}`}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selected?.id === m.id
                    ? "border-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-white/10 bg-white/[0.02] hover:border-white/25"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium tracking-tight">{m.label}</div>
                    <div className="text-[11px] text-white/50 font-mono-space mt-1">
                      LAT {m.lat.toFixed(2)}° · LON {m.lon.toFixed(2)}°
                    </div>
                  </div>
                  <MapPin className="w-4 h-4 text-white/40 shrink-0 mt-0.5" />
                </div>
                <div className="text-[11px] text-white/40 mt-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> {m.available.toLocaleString()} plots
                </div>
              </button>
            ))}
          </div>
        </aside>
      </div>

      <PlotSelectionModal
        open={open}
        onClose={() => setOpen(false)}
        marker={selected}
        planet={planet}
      />
    </div>
  );
}
